import { REPLY_TO_TICKET_CAP_PER_RUN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { ChoiceChips, Field, Input, MultiSelect, Select, Skeleton, Textarea } from "@sagot/ui";
import { useContext } from "react";
import { OptionSelect } from "../builder/action-forms";
import type { FlowContext } from "../builder/flow-context";
import { RunVariablesContext } from "../builder/hook-model";
import { useRefs } from "../builder/refs";
import type { FormProps } from "../builder/webhook-form";
import {
  ASSIGN_PICKS_IN_TEAM,
  CLIENT_TEMPLATES,
  CONTACT_AUDIENCES,
  CONTACT_CHANNELS,
  CREATE_TICKET_VARIABLES,
  DEFAULT_LINK_VARIABLE,
  LIVE_CONTACT_AUDIENCES,
  SERVICE_STEP_LIMITS,
  TICKET_ASSIGN_METHODS,
  type ClientTemplate,
  type ContactAudience,
  type ContactChannel,
  type ReplyAuthor,
  type TicketAssignMethod,
} from "./service-constants";
import { ServiceExits, ServiceVariables, SettingsLink, parseTags } from "./service-fields";
import type { AddTicketNoteStep, ApplyTicketSlaStep, AssignTicketStep, LinkTicketStep, NotifyContactStep, ReplyToTicketStep, RequestCsatStep } from "./service-types";

const F = "engage.workflows.service.fields";

type Flow = Pick<FlowContext, "paths" | "canAddPath" | "facing">;

/** Assign ticket: how the person is chosen, from which team, and what happens when nobody can take it. */
export function AssignTicketForm({ step, disabled, onChange, ...flow }: FormProps<AssignTicketStep> & Flow) {
  const { t } = useTranslation();
  const { teams, members } = useRefs();
  const picks = ASSIGN_PICKS_IN_TEAM.has(step.method);
  const setMethod = (method: TicketAssignMethod) => {
    onChange({ ...step, method, user_id: method === "user" ? step.user_id : undefined, required_skills: method === "skills_match" ? (step.required_skills ?? []) : undefined, team_id: method === "unassign" ? undefined : step.team_id });
  };
  return (
    <div className="space-y-5">
      <Field label={t(`${F}.assignMethod`)} hint={t(`${F}.assignMethodHints.${step.method}`)}>
        <Select value={step.method} disabled={disabled} onChange={(e) => { setMethod(e.target.value as TicketAssignMethod); }}>
          {TICKET_ASSIGN_METHODS.map((method) => <option key={method} value={method}>{t(`${F}.assignMethods.${method}`)}</option>)}
        </Select>
      </Field>
      {step.method === "unassign" ? null : (
        <OptionSelect label={t(picks ? `${F}.teamPick` : `${F}.teamOptional`)} value={step.team_id} options={teams} disabled={disabled} empty={t(`${F}.choose`)} onChange={(team_id) => { onChange({ ...step, team_id }); }} />
      )}
      {step.method === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
      {step.method === "skills_match" ? (
        <Field label={t(`${F}.skills`)} hint={t(`${F}.skillsHint`, { max: SERVICE_STEP_LIMITS.skills })}>
          <Input
            maxLength={SERVICE_STEP_LIMITS.skills * (SERVICE_STEP_LIMITS.skill + 2)}
            disabled={disabled}
            defaultValue={(step.required_skills ?? []).join(", ")}
            onBlur={(e) => { onChange({ ...step, required_skills: parseTags(e.target.value) }); }}
          />
        </Field>
      ) : null}
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

/** Add internal note: the words (with ticket variables), and the people it @mentions. */
export function AddTicketNoteForm({ step, disabled, onChange }: FormProps<AddTicketNoteStep>) {
  const { t } = useTranslation();
  const { members } = useRefs();
  return (
    <div className="space-y-5">
      <div>
        <Field label={t(`${F}.noteText`)} hint={t(`${F}.noteHint`)}>
          <Textarea rows={4} maxLength={SERVICE_STEP_LIMITS.text} disabled={disabled} value={step.text} onChange={(e) => { onChange({ ...step, text: e.target.value }); }} />
        </Field>
        <ServiceVariables />
      </div>
      <Field label={t(`${F}.mentions`)} hint={t(`${F}.mentionsHint`, { max: SERVICE_STEP_LIMITS.mentions })}>
        <MultiSelect
          options={members.map((member) => ({ value: member.id, label: member.name }))}
          value={step.mentions ?? []}
          disabled={disabled}
          onValueChange={(mentions) => { onChange({ ...step, mentions: mentions.length > 0 ? mentions.slice(0, SERVICE_STEP_LIMITS.mentions) : undefined }); }}
          labels={{ placeholder: t(`${F}.mentionsPlaceholder`), search: t(`${F}.search`), empty: t(`${F}.noMatches`) }}
          summary={(count) => t(`${F}.mentionsSelected`, { count })}
        />
      </Field>
    </div>
  );
}

/** Reply to ticket: a public message as the Service Desk or as a chosen member; a run writes at most three to one ticket. */
export function ReplyToTicketForm({ step, disabled, onChange }: FormProps<ReplyToTicketStep>) {
  const { t } = useTranslation();
  const { members } = useRefs();
  const author: ReplyAuthor = step.author ?? "system";
  return (
    <div className="space-y-5">
      <div>
        <Field label={t(`${F}.replyText`)} hint={t(`${F}.replyHint`)}>
          <Textarea rows={5} maxLength={SERVICE_STEP_LIMITS.text} disabled={disabled} value={step.text} onChange={(e) => { onChange({ ...step, text: e.target.value }); }} />
        </Field>
        <ServiceVariables />
      </div>
      <Field label={t(`${F}.replyAuthor`)}>
        <Select value={author} disabled={disabled} onChange={(e) => { const next = e.target.value as ReplyAuthor; onChange({ ...step, author: next, user_id: next === "user" ? step.user_id : undefined }); }}>
          <option value="system">{t(`${F}.authors.system`)}</option>
          <option value="user">{t(`${F}.authors.user`)}</option>
        </Select>
      </Field>
      {author === "user" ? <OptionSelect label={t(`${F}.member`)} value={step.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...step, user_id }); }} /> : null}
      <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" data-testid="reply-cap-note">{t(`${F}.replyCap`, { count: REPLY_TO_TICKET_CAP_PER_RUN })}</p>
    </div>
  );
}

/** Notify contact: who is told, how it reaches them, which e-mail template, and the line sent to a chat. */
export function NotifyContactForm({ step, disabled, onChange, ...flow }: FormProps<NotifyContactStep> & Flow) {
  const { t } = useTranslation();
  const channels = step.channels ?? [];
  const later = step.to.some((audience) => !LIVE_CONTACT_AUDIENCES.has(audience));
  return (
    <div className="space-y-5">
      <div>
        <span className="label">{t(`${F}.audience`)}</span>
        <ChoiceChips<ContactAudience>
          multiple
          aria-label={t(`${F}.audience`)}
          value={step.to}
          onValueChange={(picked) => { onChange({ ...step, to: CONTACT_AUDIENCES.filter((audience) => picked.includes(audience)) }); }}
          options={CONTACT_AUDIENCES.map((audience) => ({ value: audience, label: t(`${F}.audiences.${audience}`), disabled }))}
        />
        {later ? <p className="mt-1 text-xs text-fg-subtle" data-testid="audience-later">{t(`${F}.audienceLater`)}</p> : null}
      </div>
      <div>
        <span className="label">{t(`${F}.channels`)}</span>
        <ChoiceChips<ContactChannel>
          multiple
          aria-label={t(`${F}.channels`)}
          value={channels}
          onValueChange={(picked) => { onChange({ ...step, channels: CONTACT_CHANNELS.filter((channel) => picked.includes(channel)) }); }}
          options={CONTACT_CHANNELS.map((channel) => ({ value: channel, label: t(`${F}.channelNames.${channel}`), disabled }))}
        />
        <p className="mt-1 text-xs text-fg-subtle">{t(`${F}.channelsHint`)}</p>
      </div>
      <Field label={t(`${F}.template`)} hint={t(`${F}.templateHint`)}>
        <Select value={step.template} disabled={disabled} onChange={(e) => { onChange({ ...step, template: e.target.value as ClientTemplate }); }}>
          {CLIENT_TEMPLATES.map((template) => <option key={template} value={template}>{t(`${F}.templates.${template}`)}</option>)}
        </Select>
      </Field>
      <Field label={t(`${F}.chatLine`)} hint={t(`${F}.chatLineHint`)}>
        <Textarea rows={2} maxLength={SERVICE_STEP_LIMITS.text} disabled={disabled} value={step.text ?? ""} onChange={(e) => { onChange({ ...step, text: e.target.value || undefined }); }} />
      </Field>
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}

const RUN_VARIABLE = /^var\.[a-z0-9_]{1,40}$/;

/** Link ticket: the chat is linked to the ticket a run variable names (`var.ticket_number`, set by Create ticket). */
export function LinkTicketForm({ step, disabled, onChange }: FormProps<LinkTicketStep>) {
  const { t } = useTranslation();
  const known = useContext(RunVariablesContext);
  const variable = step.variable ?? "";
  const options = [...new Set<string>([...CREATE_TICKET_VARIABLES, ...known])];
  const invalid = variable !== "" && !RUN_VARIABLE.test(variable);
  return (
    <div className="space-y-5">
      <Field label={t(`${F}.linkSource`)} hint={t(`${F}.linkSourceHint`)}>
        <Select value={step.source ?? "variable"} disabled={disabled} onChange={(e) => { const source = e.target.value as "event" | "variable"; onChange({ ...step, source, variable: source === "variable" ? (step.variable ?? DEFAULT_LINK_VARIABLE) : undefined }); }}>
          <option value="variable">{t(`${F}.linkSources.variable`)}</option>
          <option value="event" disabled={step.source !== "event"}>{t(`${F}.linkSources.event`)}</option>
        </Select>
      </Field>
      {step.source === "event" ? <p className="text-xs font-semibold text-warning" role="status">{t(`${F}.linkEventLater`)}</p> : null}
      {(step.source ?? "variable") === "variable" ? (
        <Field label={t(`${F}.linkVariable`)} hint={t(`${F}.linkVariableHint`)} error={invalid ? t(`${F}.linkVariableInvalid`) : undefined}>
          <Input list={`${step.id}-variables`} maxLength={45} disabled={disabled} value={variable} invalid={invalid} onChange={(e) => { onChange({ ...step, variable: e.target.value.trim() || undefined }); }} />
          <datalist id={`${step.id}-variables`}>{options.map((name) => <option key={name} value={name} />)}</datalist>
        </Field>
      ) : null}
    </div>
  );
}

/** Apply ticket SLA: replaces the ticket's SLA policy; its clocks start again from now. */
export function ApplyTicketSlaForm({ step, disabled, onChange }: FormProps<ApplyTicketSlaStep>) {
  const { t } = useTranslation();
  const { service, loading } = useRefs();
  if (loading && !service) return <Skeleton className="h-16 w-full" aria-hidden="true" />;
  const policies = service?.policies ?? [];
  return (
    <div className="space-y-3">
      <Field label={t(`${F}.slaPolicy`)} hint={t(`${F}.slaPolicyHint`)}>
        <Select value={step.policy_id} disabled={disabled || policies.length === 0} onChange={(e) => { onChange({ ...step, policy_id: e.target.value }); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {step.policy_id && !policies.some((policy) => policy.id === step.policy_id) ? <option value={step.policy_id}>{t(`${F}.policyGone`)}</option> : null}
          {policies.map((policy) => <option key={policy.id} value={policy.id}>{policy.name}</option>)}
        </Select>
      </Field>
      {policies.length === 0 ? <p className="text-xs text-fg-muted" data-testid="sla-none">{t(`${F}.slaNone`)} <SettingsLink label={t(`${F}.openSettings`)} /></p> : null}
    </div>
  );
}

/** Request CSAT: the survey rules of the workspace decide whether the client is asked; the exits say what happened. */
export function RequestCsatForm({ step, disabled, onChange, ...flow }: FormProps<RequestCsatStep> & Flow) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <p className="text-sm text-fg-muted">{t(`${F}.csatHelp`)}</p>
      <ServiceExits step={step} disabled={disabled} flow={flow} onChange={onChange} />
    </div>
  );
}
